import { Linking } from 'react-native';
import { appConfig } from './config';
import { notify } from './dialog';

/**
 * Reaching a human on WhatsApp.
 *
 * ---------------------------------------------------------------------------
 * WHY THE NUMBER IS NOT WRITTEN AT THE CALL SITE
 * ---------------------------------------------------------------------------
 * It was, twice, and both were wrong: `data/account.ts` shipped
 * `wa.me/911234567890` and `tel:+911234567890` on the Help & Support page -
 * placeholders from the mockup that have been live the whole time. A customer
 * who tapped either got a stranger, or nothing.
 *
 * So there is one resolver, it reads ops settings first, and the literal below
 * is the last resort rather than the source.
 */

/**
 * The number this build falls back to.
 *
 * ---------------------------------------------------------------------------
 * A REAL NUMBER, NOT AN EMPTY STRING
 * ---------------------------------------------------------------------------
 * `supportPhone` defaults to `''` in `config.ts` and the rule there is "empty
 * means do not offer to call". That rule is right for the DIALLER - ringing
 * nothing is worse than no button.
 *
 * This is deliberately the other way round. Vishal's desk number is a fact
 * about the business, not about a deployment, and a WhatsApp button that
 * disappears because a settings row was never written is a support channel
 * that silently is not there. Ops overrides it the moment they set
 * `support.whatsapp` (or `support.phone`); until then the app still works.
 *
 * Digits only, country code included, no `+` - that is the form `wa.me` takes.
 */
const FALLBACK_WHATSAPP = '919993083555';

/**
 * India, for a ten-digit number typed without a country code.
 *
 * The one place a guess is made, and it is the guess every Indian number in
 * this codebase already carries.
 */
const DEFAULT_COUNTRY_CODE = '91';

/**
 * A number `wa.me` will accept, from whatever ops typed.
 *
 * `wa.me/<digits>` is strict in a way that is easy to miss: it wants digits
 * and nothing else, with the country code and no `+`. A perfectly sensible
 * setting value - `+91 99930 83555` - 404s, and so does `09993083555`,
 * because the trunk zero is not part of the international number.
 *
 * Returns `''` for anything with no digits in it, so a caller can tell "ops
 * left this blank" from "ops typed a number".
 */
export const whatsappDigits = (raw: string): string => {
  const digits = (raw ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '';
  /* Ten digits is a bare Indian mobile; anything longer already carries its
     country code and must not be prefixed again. */
  return digits.length === 10 ? `${DEFAULT_COUNTRY_CODE}${digits}` : digits;
};

/**
 * Where a WhatsApp message goes: the ops number, or this build's.
 *
 * `support.whatsapp` first because a desk that answers calls on a landline
 * cannot receive a message there - see the setting's own note on the server.
 */
export const supportWhatsappNumber = (): string => {
  const config = appConfig();
  return (
    whatsappDigits(config.supportWhatsapp) ||
    whatsappDigits(config.supportPhone) ||
    FALLBACK_WHATSAPP
  );
};

/** The same number as a human reads it: `+91 99930 83555`. */
export const supportWhatsappDisplay = (): string => {
  const digits = supportWhatsappNumber();
  const local = digits.startsWith(DEFAULT_COUNTRY_CODE)
    ? digits.slice(DEFAULT_COUNTRY_CODE.length)
    : digits;
  if (local.length !== 10) return `+${digits}`;
  return `+${DEFAULT_COUNTRY_CODE} ${local.slice(0, 5)} ${local.slice(5)}`;
};

/** The link, exported for the rows that print a URL rather than call `open`. */
export const supportWhatsappUrl = (message?: string): string => {
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${supportWhatsappNumber()}${text}`;
};

/**
 * Open the chat, with the message already typed.
 *
 * ---------------------------------------------------------------------------
 * `https://wa.me`, NOT `whatsapp://`
 * ---------------------------------------------------------------------------
 * Both open the app when it is installed. They differ on the phone that does
 * not have it: the scheme rejects and the tap does nothing, while this lands
 * on a web page that offers the install. The second is a worse conversation
 * and a better failure.
 *
 * The prefilled text is the point of doing this from inside a booking. Support
 * asking "which booking?" is a round trip that costs more than the message
 * itself, and a customer reading it back is a customer who can correct it
 * before sending - unlike a hidden reference the app attaches.
 */
/* Not `async`, and it returns nothing on purpose. Every caller is an
   `onPress`, and a promise handed to one is a promise nobody awaits - which
   is either a floating rejection or a `void` operator in front of the call.
   The one thing that can go wrong is handled here instead. */
export const openWhatsappSupport = (message?: string): void => {
  Linking.openURL(supportWhatsappUrl(message)).catch(() => {
    /*
     * Said out loud rather than swallowed.
     *
     * The other `Linking` failures in this app are silent because the number
     * is printed beside the control, so there is always a way through. This
     * one is a button with nothing written on it but a label, and a tap that
     * does nothing reads as a broken app rather than as a missing WhatsApp.
     */
    notify(
      'Couldn’t open WhatsApp',
      `Message us on ${supportWhatsappDisplay()} - or use Help & Support in your account.`,
    );
  });
};

/**
 * The number to dial, as a `tel:` link.
 *
 * `config.ts` says an empty `supportPhone` means "do not offer to call", and
 * that rule is kept: this never returns an empty number, so nothing built on
 * it can dial one. A deployment that has not set `support.phone` falls through
 * to the same handset WhatsApp goes to, which is a phone that rings.
 */
export const supportCallNumber = (): string => {
  const typed = whatsappDigits(appConfig().supportPhone);
  return typed || supportWhatsappNumber();
};

/** The dialled number as a human reads it. */
export const supportCallDisplay = (): string => {
  const digits = supportCallNumber();
  const local = digits.startsWith(DEFAULT_COUNTRY_CODE)
    ? digits.slice(DEFAULT_COUNTRY_CODE.length)
    : digits;
  if (local.length !== 10) return `+${digits}`;
  return `+${DEFAULT_COUNTRY_CODE} ${local.slice(0, 5)} ${local.slice(5)}`;
};
